import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import JourneyHeader from './components/JourneyHeader';
import JourneySidebar, { SidebarDetailRowClickPayload } from './components/JourneySidebar';
import JourneyMap from './components/JourneyMap';
import PlaybackController from './components/PlaybackController';
import useJourneyData from './hooks/useJourneyData';
import useJourneyQueryState from './hooks/useJourneyQueryState';
import usePlaybackAnimation from './hooks/usePlaybackAnimation';
import {
  JOURNEY_ERROR_AUTO_HIDE_MS,
  JOURNEY_FLY_TO_ZOOM,
  JOURNEY_RESUME_PLAYBACK_ZOOM_MAX,
  JOURNEY_RESUME_PLAYBACK_ZOOM_MIN,
} from './constants/journey';
import { JourneyPoint } from '~/utils/interface/mapback';
import { JourneyHighlightProvider, useClearHoveredSegmentIndex } from './context/JourneyHighlightContext';
import { JourneyPersonStatusProvider } from './context/JourneyPersonStatusContext';

interface PlaybackUiState {
  isPlaying: boolean;
  currentPointIndex: number;
  playSpeed: 1 | 2 | 4;
}

const Journey: React.FC = () => (
  <JourneyPersonStatusProvider>
    <JourneyHighlightProvider>
      <JourneyView />
    </JourneyHighlightProvider>
  </JourneyPersonStatusProvider>
);

const JourneyView: React.FC = () => {
  const { t } = useTranslation('admin/journey');
  const clearHoveredSegmentIndex = useClearHoveredSegmentIndex();
  const { selectedPersonId, dateRange, setDateRange } = useJourneyQueryState();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [playbackState, setPlaybackState] = useState<PlaybackUiState>({
    isPlaying: false,
    currentPointIndex: 0,
    playSpeed: 1,
  });
  const [playbackProgressIndex, setPlaybackProgressIndex] = useState(0);
  const [sidebarPointIndex, setSidebarPointIndex] = useState(0);
  const [hasPlaybackInteracted, setHasPlaybackInteracted] = useState(false);
  const [visibleError, setVisibleError] = useState<string | null>(null);
  const [selectedTimelineSegmentIndex, setSelectedTimelineSegmentIndex] = useState<number | null>(null);
  const [mapFlyToTarget, setMapFlyToTarget] = useState<{
    latitude: number;
    longitude: number;
    key: string;
    zoom?: number;
    adaptiveZoomThreshold?: number;
    adaptiveZoomMin?: number;
    resumePlaybackAfter?: boolean;
  } | null>(null);
  const pendingResumePlayRef = useRef(false);
  const [pinnedMapInfoCard, setPinnedMapInfoCard] = useState<{
    point: JourneyPoint;
    startAddress?: string;
    key: string;
  } | null>(null);
  const [sidebarSelectionMarker, setSidebarSelectionMarker] = useState<{
    latitude: number;
    longitude: number;
    key: string;
  } | null>(null);
  const [sidebarTableSelectedPointIndex, setSidebarTableSelectedPointIndex] = useState<number | null>(null);
  const [activeSidebarTab, setActiveSidebarTab] = useState<'timeline' | 'detail'>('timeline');

  const { journeyData, selectedPersonInfo, timelineItems, allPoints, isLoading, error } = useJourneyData(
    selectedPersonId,
    dateRange.start,
    dateRange.end
  );
  const hasValidDateRange = Boolean(dateRange.start && dateRange.end);

  useEffect(() => {
    const maxIndex = Math.max(0, allPoints.length - 1);
    setPlaybackState((prev) => ({
      ...prev,
      currentPointIndex: Math.min(prev.currentPointIndex, maxIndex),
      isPlaying: allPoints.length > 0 ? prev.isPlaying : false,
    }));
    setPlaybackProgressIndex((prev) => Math.min(prev, maxIndex));
    setSidebarPointIndex((prev) => Math.min(prev, maxIndex));
  }, [allPoints.length]);

  useEffect(() => {
    if (!playbackState.isPlaying) {
      setSidebarPointIndex(playbackState.currentPointIndex);
      return;
    }

    // Throttle sidebar highlight updates while playing to avoid jank on map interactions.
    const timer = window.setTimeout(() => {
      setSidebarPointIndex(playbackState.currentPointIndex);
    }, 180);

    return () => window.clearTimeout(timer);
  }, [playbackState.currentPointIndex, playbackState.isPlaying]);

  const handlePlaybackPointChange = useCallback((nextPointIndex: number) => {
    setPlaybackState((prev) => ({ ...prev, currentPointIndex: nextPointIndex }));
    setPlaybackProgressIndex(nextPointIndex);
  }, []);

  const handlePlaybackProgressChange = useCallback((nextProgress: number) => {
    setPlaybackProgressIndex(nextProgress);
  }, []);

  usePlaybackAnimation(
    allPoints,
    playbackState.isPlaying,
    playbackState.currentPointIndex,
    playbackState.playSpeed,
    handlePlaybackPointChange,
    handlePlaybackProgressChange
  );

  useEffect(() => {
    if (!error?.message) {
      setVisibleError(null);
      return;
    }

    let translated = t('errors.unknown');
    if (error.message === 'NOT_FOUND') translated = t('errors.notFound');
    if (error.message === 'NETWORK') translated = t('errors.network');

    setVisibleError(translated);
    const timer = window.setTimeout(() => setVisibleError(null), JOURNEY_ERROR_AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [error?.message, t]);

  const handleSegmentSelect = useCallback(
    (idx: number) => {
      const firstPointInSegmentIndex = allPoints.findIndex((point) => point.segmentIndex === idx);
      if (firstPointInSegmentIndex < 0) return;

      setSelectedTimelineSegmentIndex(idx);
      setHasPlaybackInteracted(true);
      setPlaybackState((prev) => ({
        ...prev,
        currentPointIndex: firstPointInSegmentIndex,
      }));
      setPlaybackProgressIndex(firstPointInSegmentIndex);
    },
    [allPoints]
  );

  const handleSidebarDetailRowClick = useCallback((row: SidebarDetailRowClickPayload) => {
    setSelectedTimelineSegmentIndex(null);
    clearHoveredSegmentIndex();
    setPinnedMapInfoCard(null);

    if (row.latitude != null && row.longitude != null) {
      setMapFlyToTarget({
        latitude: row.latitude,
        longitude: row.longitude,
        key: `${row.id}-${Date.now()}`,
        zoom: JOURNEY_FLY_TO_ZOOM,
      });
      setSidebarSelectionMarker({
        latitude: row.latitude,
        longitude: row.longitude,
        key: `sidebar-${row.id}-${Date.now()}`,
      });
    }

    if (row.pointIndex != null && row.pointIndex >= 0) {
      setSidebarTableSelectedPointIndex(row.pointIndex);
      setPlaybackState((prev) => ({ ...prev, isPlaying: false }));
    }
  }, [clearHoveredSegmentIndex]);

  const flyToTimelineSegment = useCallback(
    (segmentIndex: number) => {
      const item = timelineItems[segmentIndex];
      if (!item) return;

      const firstPoint = item.points?.[0];
      const latitude = firstPoint?.latitude ?? item.startCoord[0];
      const longitude = firstPoint?.longitude ?? item.startCoord[1];
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      setMapFlyToTarget({
        latitude,
        longitude,
        key: `timeline-${segmentIndex}-${Date.now()}`,
        zoom: JOURNEY_FLY_TO_ZOOM,
      });
    },
    [timelineItems]
  );

  const showTimelineSegmentInfoCard = useCallback(
    (segmentIndex: number) => {
      const item = timelineItems[segmentIndex];
      const firstPoint = item?.points?.[0];
      if (!firstPoint) return;

      setPinnedMapInfoCard({
        point: firstPoint,
        startAddress: item.startAddress || firstPoint.address,
        key: `timeline-info-${segmentIndex}-${Date.now()}`,
      });
    },
    [timelineItems]
  );

  const handleTimelineSegmentClick = useCallback(
    (segmentIndex: number) => {
      setSelectedTimelineSegmentIndex((prev) => {
        const nextSelectedSegmentIndex = prev === segmentIndex ? null : segmentIndex;
        if (nextSelectedSegmentIndex === null) {
          clearHoveredSegmentIndex();
          setPinnedMapInfoCard(null);
        } else {
          setSidebarTableSelectedPointIndex(null);
          setSidebarSelectionMarker(null);
          flyToTimelineSegment(nextSelectedSegmentIndex);
          showTimelineSegmentInfoCard(nextSelectedSegmentIndex);
          setPlaybackState((prev) => ({ ...prev, isPlaying: false }));
        }
        return nextSelectedSegmentIndex;
      });
    },
    [flyToTimelineSegment, showTimelineSegmentInfoCard, clearHoveredSegmentIndex]
  );

  const handleResumePlaybackFlyTo = useCallback((): boolean => {
    const pointIndex = Math.min(
      Math.max(0, Math.floor(playbackProgressIndex)),
      Math.max(0, allPoints.length - 1)
    );
    const point = allPoints[pointIndex];
    if (!point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return false;

    setMapFlyToTarget({
      latitude: point.latitude,
      longitude: point.longitude,
      key: `resume-play-${pointIndex}-${Date.now()}`,
      zoom: JOURNEY_RESUME_PLAYBACK_ZOOM_MAX,
      adaptiveZoomThreshold: JOURNEY_RESUME_PLAYBACK_ZOOM_MAX,
      adaptiveZoomMin: JOURNEY_RESUME_PLAYBACK_ZOOM_MIN,
      resumePlaybackAfter: true,
    });
    return true;
  }, [allPoints, playbackProgressIndex]);

  const clearTimelineTableSelection = useCallback(() => {
    setSelectedTimelineSegmentIndex(null);
    clearHoveredSegmentIndex();
    setPinnedMapInfoCard(null);
  }, [clearHoveredSegmentIndex]);

  const handleFlyToTargetComplete = useCallback(
    (target: NonNullable<typeof mapFlyToTarget>) => {
      if (!target.resumePlaybackAfter || !pendingResumePlayRef.current) return;
      pendingResumePlayRef.current = false;
      setPlaybackState((prev) => ({ ...prev, isPlaying: true }));
    },
    []
  );

  return (
    <Box className='min-h-screen'>
      <JourneyHeader
        dateRange={dateRange}
        onDateRangeChange={(start: Date | null, end: Date | null) => {
          setDateRange({ start, end });
          setHasPlaybackInteracted(false);
          setSelectedTimelineSegmentIndex(null);
          clearHoveredSegmentIndex();
          setMapFlyToTarget(null);
          setPinnedMapInfoCard(null);
          setSidebarSelectionMarker(null);
          setSidebarTableSelectedPointIndex(null);
          pendingResumePlayRef.current = false;
          setPlaybackState((prev) => ({ ...prev, currentPointIndex: 0, isPlaying: false }));
          setPlaybackProgressIndex(0);
        }}
        isLoading={isLoading}
      />

      <Box className='mx-auto flex min-h-screen w-full  flex-col'>
        {visibleError && (
          <Box className='border-b border-red-100 bg-red-50 px-6 py-3 text-sm text-red-700'>{visibleError}</Box>
        )}

        <Box className='grid grid-cols-1 gap-5 px-[26px] pb-4 pt-4 md:grid-cols-[420px_minmax(0,1fr)] md:items-start'>
          <Box className='w-full'>
            <JourneySidebar
              journeyData={journeyData}
              selectedPersonInfo={selectedPersonInfo}
              dateRange={dateRange}
              timelineItems={timelineItems}
              allPoints={allPoints}
              selectedDate={selectedDate}
              onSelectedDateChange={setSelectedDate}
              isLoading={isLoading}
              hasValidDateRange={hasValidDateRange}
              currentPointIndex={
                playbackState.isPlaying
                  ? sidebarPointIndex
                  : (sidebarTableSelectedPointIndex ?? sidebarPointIndex)
              }
              enableActiveHighlight={
                hasPlaybackInteracted || playbackState.isPlaying || sidebarTableSelectedPointIndex !== null
              }
              onDetailRowClick={handleSidebarDetailRowClick}
              activeTab={activeSidebarTab}
              onTabChange={setActiveSidebarTab}
              onTimelineSegmentClick={handleTimelineSegmentClick}
              selectedTimelineSegmentIndex={selectedTimelineSegmentIndex}
              onTimelineSegmentDoubleClick={(segmentIndex) => {
                setSelectedTimelineSegmentIndex((prev) => {
                  const next = prev === segmentIndex ? null : prev;
                  if (next === null) {
                    setPinnedMapInfoCard(null);
                  }
                  return next;
                });
              }}
            />
          </Box>

          <Box className='w-full flex flex-col'>
            <Box className='flex flex-1 flex-col gap-4 rounded-[12px] bg-white px-3 pb-4 pt-3'>
              <Typography
                component='h3'
                className='flex h-[25px] items-center justify-between text-[18px] font-semibold leading-[140%] text-[#161A1F]'
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('map.title')}
              </Typography>

              <Box className='relative h-[calc(100vh-14.375rem)] min-h-[560px] overflow-hidden rounded-[8px] border border-[#D7DEE7] bg-white'>
                <Box className='absolute inset-0'>
                  <JourneyMap
                    allPoints={allPoints}
                    timelineItems={timelineItems}
                    playbackProgressIndex={playbackProgressIndex}
                    isPlaying={playbackState.isPlaying}
                    currentPointIndex={playbackState.currentPointIndex}
                    playSpeed={playbackState.playSpeed}
                    onPlaybackProgressChange={handlePlaybackProgressChange}
                    onSegmentClick={handleSegmentSelect}
                    selectedSegmentIndex={selectedTimelineSegmentIndex}
                    flyToTarget={mapFlyToTarget}
                    onFlyToTargetComplete={handleFlyToTargetComplete}
                    pinnedInfoCard={pinnedMapInfoCard}
                    sidebarSelectionMarker={sidebarSelectionMarker}
                    selectedPersonLocation={{
                      latitude: selectedPersonInfo?.latitude ?? null,
                      longitude: selectedPersonInfo?.longitude ?? null,
                    }}
                    fullName={selectedPersonInfo?.full_name || journeyData?.full_name || ''}
                    isLoading={isLoading}
                  />
                </Box>

                <Box className='absolute inset-x-0 bottom-0 z-10'>
                  <PlaybackController
                    totalSegments={allPoints.length}
                    currentSegmentIndex={playbackProgressIndex}
                    onSegmentChange={(idx) => {
                      setHasPlaybackInteracted(true);
                      const nextIndex = Math.max(0, Math.round(idx));
                      setPlaybackState((prev) => ({ ...prev, currentPointIndex: nextIndex }));
                      setPlaybackProgressIndex(nextIndex);
                    }}
                    isPlaying={playbackState.isPlaying}
                    onPlayChange={(playing) => {
                      setHasPlaybackInteracted(true);
                      if (playing) {
                        clearHoveredSegmentIndex();
                        if (!playbackState.isPlaying) {
                          const willFlyBeforeResume = handleResumePlaybackFlyTo();
                          setSidebarTableSelectedPointIndex(null);
                          setSidebarSelectionMarker(null);
                          clearTimelineTableSelection();
                          if (!willFlyBeforeResume) {
                            setPlaybackState((prev) => ({ ...prev, isPlaying: true }));
                            return;
                          }
                          pendingResumePlayRef.current = true;
                          return;
                        }
                        setPlaybackState((prev) => ({ ...prev, isPlaying: true }));
                        return;
                      }

                      pendingResumePlayRef.current = false;
                      setPlaybackState((prev) => ({ ...prev, isPlaying: false }));
                    }}
                    playSpeed={playbackState.playSpeed}
                    onSpeedChange={(speed) => setPlaybackState((prev) => ({ ...prev, playSpeed: speed }))}
                    isLoading={isLoading}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Journey;
